import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { accessLocalStorage } from '../../../services';
import callInactivityApi from '../../../services/callInactivityApi';

import Loading from '../../Loading/LoadingModule';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory,
  faSync,
  faFilter,
  faCheckCircle,
  faExclamationTriangle,
  faBell
} from '@fortawesome/free-solid-svg-icons';

import '../../../libs/bulma.min.css';
import './CallInactivityHistory.css';

function CallInactivityHistory() {
  const history = useHistory();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [userTenant, setUserTenant] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [filters, setFilters] = useState({
    alert_type: '',
    resolved: '',
    limit: 50
  });

  const loadAlerts = async (tenant, currentFilters) => {
    try {
      setRefreshing(true);
      const filterParams = {};
      if (currentFilters.alert_type) filterParams.alert_type = currentFilters.alert_type;
      if (currentFilters.resolved !== '') filterParams.resolved = currentFilters.resolved;
      if (currentFilters.limit) filterParams.limit = currentFilters.limit;

      const response = await callInactivityApi.getAlertHistory(tenant, filterParams);
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setAlerts([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const user = await accessLocalStorage.getUserLocalStorage();
        if (!user?.user?.idtenant) {
          history.push('/');
          return;
        }

        const tenant = user.user.idtenant;
        console.log('✅ [CALL-INACTIVITY-HISTORY] Tenant identificado:', tenant);
        setUserTenant(tenant);
        
        await loadAlerts(tenant, filters);
        setInitialized(true);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (userTenant && initialized && !loading) {
      loadAlerts(userTenant, filters);
    }
  }, [filters.alert_type, filters.resolved, filters.limit]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAlertTypeBadge = (type) => {
    if (type === 'inbound') {
      return <span className="history-badge badge-inbound">Entrada</span>;
    }
    return <span className="history-badge badge-outbound">Saída</span>;
  };

  const getStatusBadge = (resolvedAt) => {
    if (resolvedAt) {
      return <span className="history-badge badge-resolved">Resolvido</span>;
    }
    return <span className="history-badge badge-active">Ativo</span>;
  };

  const handleRefresh = () => {
    if (userTenant) {
      loadAlerts(userTenant, filters);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="call-inactivity-history-container">
      <div className="call-inactivity-history-header">
        <div className="call-inactivity-history-title-wrapper">
          <svg className="call-inactivity-history-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h1 className="call-inactivity-history-title">Histórico de Alertas</h1>
            <p className="call-inactivity-history-subtitle">Visualize todos os alertas de inatividade enviados</p>
          </div>
        </div>
        <div className="call-inactivity-history-actions">
          <button 
            className={`btn-refresh-history ${refreshing ? 'is-loading' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <FontAwesomeIcon icon={faSync} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="filter-card">
        <div className="filter-header">
          <FontAwesomeIcon icon={faFilter} className="filter-icon" />
          <span className="filter-title">Filtros</span>
        </div>
        <div className="filter-row">
          <div className="filter-field">
            <label className="filter-label">Tipo de Alerta</label>
            <select
              name="alert_type"
              value={filters.alert_type}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="inbound">Entrada</option>
              <option value="outbound">Saída</option>
            </select>
          </div>

          <div className="filter-field">
            <label className="filter-label">Status</label>
            <select
              name="resolved"
              value={filters.resolved}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="false">Ativos</option>
              <option value="true">Resolvidos</option>
            </select>
          </div>

          <div className="filter-field">
            <label className="filter-label">Quantidade</label>
            <select
              name="limit"
              value={filters.limit}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="10">10 registros</option>
              <option value="25">25 registros</option>
              <option value="50">50 registros</option>
              <option value="100">100 registros</option>
            </select>
          </div>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faBell} />
          </div>
          <h2 className="empty-state-title">Nenhum Alerta Encontrado</h2>
          <p className="empty-state-text">
            Não há alertas de inatividade com os filtros selecionados.
          </p>
        </div>
      ) : (
        <>
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead className="history-table-header">
                <tr>
                  <th>Data do Alerta</th>
                  <th>Tipo</th>
                  <th>Última Chamada</th>
                  <th>Inatividade</th>
                  <th>Email Enviado</th>
                  <th>Status</th>
                  <th>Resolvido em</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => (
                  <tr key={alert.id_call_inactivity_alerts}>
                    <td>{formatDate(alert.alert_sent_at)}</td>
                    <td>{getAlertTypeBadge(alert.alert_type)}</td>
                    <td>{formatDate(alert.last_call_date)}</td>
                    <td>
                      <span className="history-badge badge-hours">
                        {alert.inactivity_hours}h
                      </span>
                    </td>
                    <td className="email-cell">{alert.email_sent_to}</td>
                    <td>{getStatusBadge(alert.resolved_at)}</td>
                    <td>{formatDate(alert.resolved_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="history-stats">
            <p className="history-stats-text">
              Exibindo <strong>{alerts.length}</strong> alerta{alerts.length !== 1 ? 's' : ''}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default CallInactivityHistory;
